

import { Plus, Wrench, Building2, Package, Trash2, ArrowRightLeft } from "lucide-react";
import { formatCurrencyFull } from "@/lib/data";
import { ServiceItem, VendorItem } from "./types";

interface DetailedItemAllocationsProps {
  services: ServiceItem[];
  vendors: VendorItem[];
  onAddService?: () => void;
  onAddVendor?: () => void;
  onAssignVendor?: (service: ServiceItem) => void;
  onSwitchVendor?: (item: ServiceItem | VendorItem, type: "service" | "vendor") => void;
  onDeleteItem?: (item: ServiceItem | VendorItem, type: "service" | "vendor") => void;
}

function TypeBadge({ type }: { type: "service" | "vendor" }) {
  if (type === "service") {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-violet-100 text-violet-700" title="Service">
        <Wrench className="w-3 h-3" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-100 text-blue-700" title="Vendor">
      <Building2 className="w-3 h-3" />
    </span>
  );
}

export default function DetailedItemAllocations({
  services,
  vendors,
  onAddService,
  onAddVendor,
  onAssignVendor,
  onSwitchVendor,
  onDeleteItem,
}: DetailedItemAllocationsProps) {
  const hasServices = services && services.length > 0;
  const hasVendors = vendors && vendors.length > 0;
  const totalItems = (services?.length || 0) + (vendors?.length || 0);

  // Calculate totals from items (this is the correct source of truth)
  const serviceTotal = (services || []).reduce((sum, s) => sum + (s.billed || 0), 0);
  const serviceCost = (services || []).reduce((sum, s) => sum + (s.cost || s.balance || 0), 0);
  const vendorTotal = (vendors || []).reduce((sum, v) => sum + (v.billed || 0), 0);
  const vendorCost = (vendors || []).reduce((sum, v) => sum + (v.cost || v.balance || 0), 0);

  // Calculate grand totals from actual items
  const grandTotalBilled = serviceTotal + vendorTotal;
  const grandTotalCost = serviceCost + vendorCost;
  const grandTotalProfit = grandTotalBilled - grandTotalCost;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      {/* Header - Compact */}
      <div className="px-4 py-3 bg-gradient-to-r from-stone-50 to-stone-100 border-b border-stone-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <Package className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-stone-800 text-sm">Item Allocations</h3>
            <p className="text-[10px] text-stone-500">{totalItems} items</p>
          </div>
        </div>
        <div className="flex gap-1">
          {onAddService && (
            <button
              onClick={onAddService}
              className="text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-md font-medium transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Service
            </button>
          )}
          {onAddVendor && (
            <button
              onClick={onAddVendor}
              className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Vendor
            </button>
          )}
        </div>
      </div>

      {/* Compact Summary Stats */}
      <div className="flex items-center gap-6 px-4 py-3 bg-stone-50 border-b border-stone-200 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-stone-500">Total:</span>
          <span className="font-bold text-stone-800">{formatCurrencyFull(grandTotalBilled)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-stone-500">Cost:</span>
          <span className="font-bold text-rose-600">{formatCurrencyFull(grandTotalCost)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-stone-500">Profit:</span>
          <span className={`font-bold ${grandTotalProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {formatCurrencyFull(grandTotalProfit)}
          </span>
        </div>
      </div>

      {/* Desktop Table - Compact */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200">
              <th className="text-left px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider w-16">
                Type
              </th>
              <th className="text-left px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider">
                Item
              </th>
              <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider w-10">
                Qty
              </th>
              <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider">
                Billed
              </th>
              <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider">
                Cost
              </th>
              <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider">
                Profit
              </th>
              <th className="text-center px-3 py-2 font-semibold text-stone-500 uppercase tracking-wider w-24">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {/* Services */}
            {hasServices && services.map((service, index) => {
              const cost = service.cost ?? service.balance ?? 0;
              const profit = service.profit ?? service.billed - cost;
              return (
                <tr key={`service-${index}`} className="hover:bg-stone-50/50 border-b border-stone-50">
                  <td className="px-3 py-2">
                    <TypeBadge type="service" />
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-stone-800 text-xs">{service.name}</p>
                    <p className="text-[10px] text-stone-400 truncate">{service.service}</p>
                  </td>
                  <td className="px-3 py-2 text-right text-stone-600">{service.quantity}</td>
                  <td className="px-3 py-2 text-right font-medium text-stone-800">
                    {formatCurrencyFull(service.billed)}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-500">
                    {formatCurrencyFull(cost)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-medium text-xs ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {formatCurrencyFull(profit)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {!service.vendor_id && onAssignVendor ? (
                      <button
                        onClick={() => onAssignVendor(service)}
                        className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                      >
                        Assign
                      </button>
                    ) : service.vendor_id ? (
                      <span className="text-xs text-emerald-600 font-semibold">✓ Assigned</span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {/* Vendors */}
            {hasVendors && vendors.map((vendor, index) => {
              const cost = vendor.cost ?? vendor.balance ?? 0;
              const profit = vendor.profit ?? vendor.billed - cost;
              return (
                <tr key={`vendor-${index}`} className="hover:bg-stone-50/50 border-b border-stone-50">
                  <td className="px-3 py-2">
                    <TypeBadge type="vendor" />
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-stone-800 text-xs">{vendor.name}</p>
                    <p className="text-[10px] text-stone-400 truncate">{vendor.service}</p>
                  </td>
                  <td className="px-3 py-2 text-right text-stone-600">{vendor.quantity}</td>
                  <td className="px-3 py-2 text-right font-medium text-stone-800">
                    {formatCurrencyFull(vendor.billed)}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-500">
                    {formatCurrencyFull(cost)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-medium text-xs ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {formatCurrencyFull(profit)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center space-x-1">
                    {onSwitchVendor && (
                      <button
                        onClick={() => onSwitchVendor(vendor, "vendor")}
                        className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors inline-flex items-center gap-1"
                        title="Switch Vendor"
                      >
                        <ArrowRightLeft className="w-3 h-3" />
                      </button>
                    )}
                    {onDeleteItem && (
                      <button
                        onClick={() => onDeleteItem(vendor, "vendor")}
                        className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors inline-flex items-center gap-1"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-stone-50 border-t border-stone-200 font-bold">
              <td className="px-3 py-2 text-stone-800 text-xs" colSpan={3}>
                Totals
              </td>
              <td className="px-3 py-2 text-right text-stone-800 text-xs">
                {formatCurrencyFull(grandTotalBilled)}
              </td>
              <td className="px-3 py-2 text-right text-rose-600 text-xs">
                {formatCurrencyFull(grandTotalCost)}
              </td>
              <td className="px-3 py-2 text-right text-xs">
                <span className={grandTotalProfit >= 0 ? "text-emerald-700" : "text-red-700"}>
                  {formatCurrencyFull(grandTotalProfit)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-stone-100">
        {/* Services */}
        {hasServices && services.map((service, index) => {
          const cost = service.cost ?? service.balance ?? 0;
          const profit = service.profit ?? service.billed - cost;
          const margin = service.profit_percent ?? (service.billed > 0 ? Math.round((profit / service.billed) * 100) : 0);
          return (
            <div key={`mobile-service-${index}`} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <TypeBadge type="service" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-800 text-sm truncate">{service.name}</p>
                  <p className="text-xs text-stone-500">{service.service}</p>
                  <p className="text-xs text-stone-400 mt-1">Qty: {service.quantity}</p>
                </div>
              </div>
              {/* Financial details with labels */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Price</p>
                  <p className="text-sm font-bold text-stone-800">{formatCurrencyFull(service.billed)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Cost</p>
                  <p className="text-sm font-bold text-rose-500">{formatCurrencyFull(cost)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Net</p>
                  <p className={`text-sm font-bold ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {formatCurrencyFull(profit)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        {/* Vendors */}
        {hasVendors && vendors.map((vendor, index) => {
          const cost = vendor.cost ?? vendor.balance ?? 0;
          const profit = vendor.profit ?? vendor.billed - cost;
          return (
            <div key={`mobile-vendor-${index}`} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <TypeBadge type="vendor" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-800 text-sm truncate">{vendor.name}</p>
                  <p className="text-xs text-stone-500">{vendor.service}</p>
                  <p className="text-xs text-stone-400 mt-1">Qty: {vendor.quantity}</p>
                </div>
              </div>
              {/* Financial details with labels */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Price</p>
                  <p className="text-sm font-bold text-stone-800">{formatCurrencyFull(vendor.billed)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Cost</p>
                  <p className="text-sm font-bold text-rose-500">{formatCurrencyFull(cost)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Net</p>
                  <p className={`text-sm font-bold ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {formatCurrencyFull(profit)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        {/* Mobile Totals */}
        <div className="px-4 py-3 bg-stone-50">
          <p className="text-xs font-semibold text-stone-600 mb-2">Grand Totals</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] text-stone-400 uppercase mb-0.5">Price</p>
              <p className="text-sm font-bold text-stone-800">{formatCurrencyFull(grandTotalBilled)}</p>
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase mb-0.5">Cost</p>
              <p className="text-sm font-bold text-rose-600">{formatCurrencyFull(grandTotalCost)}</p>
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase mb-0.5">Net</p>
              <p className={`text-sm font-bold ${grandTotalProfit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                {formatCurrencyFull(grandTotalProfit)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!hasServices && !hasVendors && (
        <div className="p-8 text-center">
          <Package className="w-12 h-12 text-stone-200 mx-auto mb-3" />
          <p className="text-stone-400 text-sm mb-4">No services or vendors assigned yet</p>
          <div className="flex gap-2 justify-center">
            {onAddService && (
              <button
                onClick={onAddService}
                className="text-sm px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Service
              </button>
            )}
            {onAddVendor && (
              <button
                onClick={onAddVendor}
                className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Vendor
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
